import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Normalize common phone formats to Twilio E.164.
 *
 * Supports:
 *   +14693015183 | 4693015183 | 469-301-5183 | (469) 301-5183 | 1-469-301-5183
 *
 * Returns null if empty or unparseable.
 */
export function tryNormalizeToE164(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  let parsed = parsePhoneNumberFromString(trimmed);
  if (parsed?.isValid()) return parsed.format('E.164');

  parsed = parsePhoneNumberFromString(trimmed, 'US');
  if (parsed?.isValid()) return parsed.format('E.164');

  const digits = trimmed.replace(/\D/g, '');
  if (digits) {
    parsed = parsePhoneNumberFromString(`+${digits}`);
    if (parsed?.isValid()) return parsed.format('E.164');
    parsed = parsePhoneNumberFromString(digits, 'US');
    if (parsed?.isValid()) return parsed.format('E.164');
  }

  return null;
}

export function parseStoredPhone(stored: string | null | undefined): {
  dialCode: string;
  nationalNumber: string;
  unparseable: boolean;
  e164: string | null;
  raw: string;
} {
  const raw = (stored ?? '').trim();
  const e164 = tryNormalizeToE164(raw);
  if (!e164) {
    return {
      dialCode: '1',
      nationalNumber: raw,
      unparseable: Boolean(raw),
      e164: null,
      raw,
    };
  }

  const parsed = parsePhoneNumberFromString(e164)!;
  return {
    dialCode: String(parsed.countryCallingCode),
    nationalNumber: parsed.nationalNumber,
    unparseable: false,
    e164,
    raw,
  };
}

export function composeE164(
  dialCode: string,
  nationalNumber: string
): { value: string; error: string } {
  const code = dialCode.replace(/\D/g, '');
  const digits = nationalNumber.replace(/\D/g, '');

  if (!digits) {
    return { value: '', error: '' };
  }

  if (!code) {
    return {
      value: '',
      error: 'Enter a country code (e.g. 1 for US).',
    };
  }

  const e164 = tryNormalizeToE164(`+${code}${digits}`);
  if (!e164) {
    return {
      value: '',
      error: 'Invalid mobile number. Example: +1 and xxx-xxx-xxxx',
    };
  }

  return { value: e164, error: '' };
}
